import React, { useState } from "react";
import {
  ChevronRight,
  ChevronLeft,
  FileText,
  Loader2,
  CheckCircle,
  Briefcase,
} from "lucide-react";

const API_WEBHOOK =
  "https://aiuhf.app.n8n.cloud/webhook/d5db03f5-3a7f-4343-9aad-aaec215f75f7";

function JobDescription() {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState({
    position: "",
    jobDescription: "",
  });

  const getActivePositions = () => {
    const stored = JSON.parse(localStorage.getItem("positions_config")) || [];
    return stored
      .filter((p) => p.status === "active")
      .map((p) => p.name.toLowerCase());
  };

  const positions = getActivePositions();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError("");
    setSuccess(false);
  };

  const handleNext = () => {
    if (!formData.position) {
      setError("Please select a position");
      return;
    }
    setStep(2);
    setError("");
    setSuccess(false);
  };

  const handleBack = () => {
    setStep(1);
    setError("");
    setSuccess(false);
  };

  const submitForm = async (e) => {
    e.preventDefault();

    if (!formData.jobDescription.trim()) {
      setError("Please enter a job description");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess(false);

    try {
      const response = await fetch(API_WEBHOOK, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          position: formData.position,
          jobDescription: formData.jobDescription,
        }),
      });

      const data = await response.json();

      if (data.success !== false) {
        setSuccess(true);
        setFormData({ position: "", jobDescription: "" });
        setStep(1);
      } else {
        setError(data.message || "Submission failed. Please try again.");
      }
    } catch (err) {
      console.error(err);
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.pageContainer}>
      <div style={styles.container}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerContent}>
            <div style={styles.iconCircle}>
              <FileText size={32} color="#fff" />
            </div>
            <div>
              <h1 style={styles.title}>Job Description</h1>
              <p style={styles.subtitle}>
                Submit job descriptions for open positions
              </p>
            </div>
          </div>
        </div>
        <div style={styles.progressContainer}>
          <div style={styles.progressBar}>
            {["Position", "Description"].map((label, i) => (
              <React.Fragment key={label}>
                {i > 0 && (
                  <div
                    style={{
                      ...styles.progressLine,
                      ...(step > i ? styles.progressLineActive : {}),
                    }}
                  />
                )}
                <div style={styles.progressStep}>
                  <div
                    style={{
                      ...styles.stepNumber,
                      ...(step >= i + 1
                        ? {
                            background:
                              "linear-gradient(135deg, #004687, #003366)",
                            color: "#fff",
                          }
                        : {}),
                    }}
                  >
                    {i + 1}
                  </div>
                  <span style={styles.stepLabel}>{label}</span>
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Form Card */}
        <div style={styles.formCard}>
          {/* Success Banner */}
          {success && (
            <div style={styles.successBanner}>
              <CheckCircle size={20} color="#10b981" />
              Job description submitted successfully!
            </div>
          )}

          {/* Step 1: Position Selection */}
          {step === 1 && (
            <div style={styles.stepContainer}>
              <h2 style={styles.stepTitle}>Select Position</h2>
              <p style={styles.stepDescription}>
                Choose the position you want to create a job description for
              </p>

              <div style={styles.formGroup}>
                <label style={styles.label}>
                  <Briefcase size={16} style={styles.labelIcon} />
                  Position <span style={styles.required}>*</span>
                </label>
                <div style={styles.selectWrapper}>
                  <select
                    name="position"
                    value={formData.position}
                    onChange={handleInputChange}
                    style={styles.select}
                  >
                    <option value="">-- Choose a Position --</option>
                    {positions.map((pos) => (
                      <option key={pos} value={pos}>
                        {pos}
                      </option>
                    ))}
                  </select>
                </div>
                {positions.length === 0 && (
                  <p style={styles.hint}>
                    ⚠️ No active positions found. Please add positions in
                    Configuration.
                  </p>
                )}
              </div>

              {error && <div style={styles.errorMessage}>⚠️ {error}</div>}

              <div style={styles.buttonGroup}>
                <button
                  onClick={handleNext}
                  style={{ ...styles.button, ...styles.primaryButton }}
                  disabled={!formData.position}
                >
                  Next
                  <ChevronRight size={20} />
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Job Description Input */}
          {step === 2 && (
            <div style={styles.stepContainer}>
              <h2 style={styles.stepTitle}>Enter Job Description</h2>
              <p style={styles.stepDescription}>
                Position:{" "}
                <strong style={{ color: "#004687" }}>{formData.position}</strong>
              </p>

              <div style={styles.formGroup}>
                <label style={styles.label}>
                  <FileText size={16} style={styles.labelIcon} />
                  Job Description <span style={styles.required}>*</span>
                </label>
                <textarea
                  name="jobDescription"
                  value={formData.jobDescription}
                  onChange={handleInputChange}
                  placeholder={`Describe the job requirements, responsibilities, qualifications, skills, experience level, and any special requirements for the ${formData.position} role...`}
                  rows="12"
                  style={styles.textarea}
                />
                <p style={styles.hint}>
                  💡 Be specific about required skills, responsibilities,
                  qualifications, and experience level for best results.
                </p>
              </div>

              {error && <div style={styles.errorMessage}>⚠️ {error}</div>}

              <div style={styles.buttonGroup}>
                <button
                  onClick={handleBack}
                  style={{ ...styles.button, ...styles.secondaryButton }}
                  disabled={loading}
                >
                  <ChevronLeft size={20} />
                  Back
                </button>
                <button
                  onClick={submitForm}
                  style={{ ...styles.button, ...styles.primaryButton }}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2
                        size={20}
                        style={{ animation: "spin 1s linear infinite" }}
                      />
                      Submitting...
                    </>
                  ) : (
                    <>
                      Submit
                      <ChevronRight size={20} />
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

const styles = {
  pageContainer: {
    minHeight: "100vh",
    padding: "40px 20px",
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  container: { maxWidth: "900px", margin: "0 auto" },
  header: {
    background: "rgba(255, 255, 255, 0.95)",
    backdropFilter: "blur(10px)",
    borderRadius: "20px",
    padding: "30px",
    marginBottom: "30px",
    boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
  },
  headerContent: { display: "flex", alignItems: "center", gap: "20px" },
  iconCircle: {
    width: "70px",
    height: "70px",
    borderRadius: "50%",
    background: "linear-gradient(135deg, #004687, #003366)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 10px 30px rgba(0, 70, 135, 0.4)",
    flexShrink: 0,
  },
  title: { margin: 0, fontSize: "32px", fontWeight: "700", color: "#004687" },
  subtitle: { margin: "5px 0 0 0", color: "#64748b", fontSize: "16px" },
  progressContainer: {
    background: "rgba(255, 255, 255, 0.95)",
    backdropFilter: "blur(10px)",
    borderRadius: "20px",
    padding: "30px",
    marginBottom: "30px",
    boxShadow: "0 10px 40px rgba(0, 0, 0, 0.2)",
  },
  progressBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "20px",
  },
  progressStep: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "10px",
    flex: "0 0 auto",
  },
  stepNumber: {
    width: "50px",
    height: "50px",
    borderRadius: "50%",
    background: "#e2e8f0",
    color: "#64748b",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "20px",
    fontWeight: "700",
    transition: "all 0.3s ease",
  },
  stepLabel: { fontSize: "14px", fontWeight: "600", color: "#64748b" },
  progressLine: {
    flex: "1",
    height: "4px",
    background: "#e2e8f0",
    borderRadius: "2px",
    transition: "all 0.3s ease",
    minWidth: "120px",
  },
  progressLineActive: {
    background: "linear-gradient(90deg, #004687, #003366)",
  },
  formCard: {
    background: "rgba(255, 255, 255, 0.95)",
    backdropFilter: "blur(10px)",
    borderRadius: "20px",
    padding: "40px",
    boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
    animation: "fadeIn 0.5s ease",
  },
  stepContainer: { animation: "fadeIn 0.5s ease" },
  stepTitle: {
    margin: "0 0 10px 0",
    fontSize: "28px",
    fontWeight: "700",
    color: "#1e293b",
  },
  stepDescription: {
    margin: "0 0 30px 0",
    color: "#64748b",
    fontSize: "16px",
  },
  formGroup: { marginBottom: "25px" },
  label: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "10px",
    fontSize: "14px",
    fontWeight: "600",
    color: "#334155",
  },
  labelIcon: { color: "#004687" },
  required: { color: "#ef4444" },
  selectWrapper: { position: "relative" },
  select: {
    width: "100%",
    padding: "14px 18px",
    fontSize: "15px",
    border: "2px solid #e2e8f0",
    borderRadius: "12px",
    outline: "none",
    transition: "all 0.3s ease",
    backgroundColor: "#fff",
    cursor: "pointer",
    appearance: "none",
    boxSizing: "border-box",
  },
  textarea: {
    width: "100%",
    padding: "14px 18px",
    fontSize: "15px",
    border: "2px solid #e2e8f0",
    borderRadius: "12px",
    outline: "none",
    transition: "all 0.3s ease",
    fontFamily: "inherit",
    resize: "vertical",
    backgroundColor: "#fff",
    boxSizing: "border-box",
    lineHeight: "1.6",
  },
  hint: { marginTop: "8px", fontSize: "13px", color: "#64748b" },
  buttonGroup: { display: "flex", gap: "15px", marginTop: "30px" },
  button: {
    flex: "1",
    padding: "16px 28px",
    fontSize: "16px",
    fontWeight: "600",
    border: "none",
    borderRadius: "12px",
    cursor: "pointer",
    transition: "all 0.3s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    outline: "none",
  },
  primaryButton: {
    background: "linear-gradient(135deg, #004687, #003366)",
    color: "#fff",
    boxShadow: "0 10px 30px rgba(0, 70, 135, 0.4)",
  },
  secondaryButton: { background: "#e2e8f0", color: "#475569" },
  errorMessage: {
    padding: "16px 20px",
    background: "#fee2e2",
    color: "#dc2626",
    borderRadius: "12px",
    marginTop: "20px",
    fontSize: "14px",
    fontWeight: "500",
    border: "2px solid #fecaca",
  },
  successBanner: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "16px 20px",
    background: "#d1fae5",
    color: "#065f46",
    borderRadius: "12px",
    marginBottom: "24px",
    fontSize: "15px",
    fontWeight: "600",
    border: "2px solid #6ee7b7",
  },
};

export default JobDescription;